import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import i18n from '../../i18n';

/**
 * Global Error Boundary for FinMaple.
 *
 * Catches unhandled JS errors in the React component tree and displays a
 * user-friendly fallback UI with retry / navigate-home options.
 *
 * Must be a class component — React does not support error boundaries via hooks.
 * Uses i18n.t() directly (no hooks) with Spanish hardcoded fallbacks so the
 * boundary is safe even if i18n fails to load.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log details in development so developers can diagnose
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary] Caught error:', error);
      console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    const lng = i18n.language || 'es';
    window.location.href = `/${lng}/`;
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Translated labels with Spanish fallbacks
    const title = i18n.t('errorBoundary.title', 'Algo salio mal');
    const message = i18n.t(
      'errorBoundary.message',
      'Lo sentimos, ocurrio un error inesperado. Puedes intentar recargar la pagina.',
    );
    const retryLabel = i18n.t('errorBoundary.retry', 'Reintentar');
    const goHomeLabel = i18n.t('errorBoundary.goHome', 'Volver al inicio');

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          px: 3,
          py: 6,
          textAlign: 'center',
        }}
      >
        {/* Error icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: '#FFEBEE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 40, color: '#C62828' }} />
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            color: '#1A1A1A',
            mb: 1.5,
          }}
        >
          {title}
        </Typography>

        {/* Message */}
        <Typography
          variant="body1"
          sx={{
            color: '#5F6B7A',
            maxWidth: 480,
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {message}
        </Typography>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={this.handleRetry}
            sx={{
              bgcolor: '#1E3A5F',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': { bgcolor: '#1A3152' },
            }}
          >
            {retryLabel}
          </Button>

          <Button
            variant="outlined"
            onClick={this.handleGoHome}
            sx={{
              borderColor: '#EEF0F3',
              color: '#1E3A5F',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#1E3A5F',
                bgcolor: 'rgba(30, 58, 95, 0.04)',
              },
            }}
          >
            {goHomeLabel}
          </Button>
        </Box>

        {/* Stack trace in development only */}
        {process.env.NODE_ENV === 'development' && this.state.error && (
          <Box
            sx={{
              mt: 4,
              p: 2,
              maxWidth: 600,
              width: '100%',
              bgcolor: '#F6F7F9',
              border: '1px solid #EEF0F3',
              borderRadius: '8px',
              textAlign: 'left',
              overflow: 'auto',
            }}
          >
            <Typography
              variant="caption"
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: 12,
                color: '#C62828',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                m: 0,
              }}
            >
              {this.state.error.toString()}
              {this.state.errorInfo?.componentStack}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
}

export default ErrorBoundary;
